import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import sortBy from 'lodash-es/sortBy';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  throttleTime,
} from 'rxjs';

import { type TagColorPalette, TagOrder } from '@blaze-runtime/engine-domain';
import { ButtonIconComponent, TooltipDirective } from '@blaze-shared/shared-ui';
import { measureTextSize, resizeRxUtils, toFloat } from '@blaze-shared/shared-util';

import { CellValueUpdateFromTag } from '../../elements/table/inline-editor/base-editor/inline-editor.types';
import { type UITableCellStyling } from '../../elements/table/table-cell-styling';
import { StylesForTagPipe } from '../../elements/tags/styles-for-tag.pipe';

interface StyleData {
  font: string;
  tagPaddingX: number;
  tagMinWidth: number;
  tagHeight: number;
  tagGap: number;
  tagWidth: number;
  listGap: number;
  containerWidth: number;
  containerHeight: number;
}

interface DisplayData {
  tags: string[];
  hiddenTagsTooltip?: string;
  wrap?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'blaze-tags-column-value',
  imports: [
    AsyncPipe,
    NgStyle,
    NgTemplateOutlet,
    MatIconModule,
    MatMenuModule,
    TooltipDirective,
    ButtonIconComponent,
    StylesForTagPipe,
  ],
  templateUrl: './tags-column-value.component.html',
  styleUrls: ['./tags-column-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsColumnValueComponent implements AfterViewInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input() palette?: TagColorPalette;
  @Input() useExternalClickAction? = false;
  @Input() useExternalValueClickAction? = false;
  @Input() isMultipleValue? = false;
  @Input() cellStyles? = {} as UITableCellStyling;
  @Input() tooltipHeader?: string;
  @Output() clickAction = new EventEmitter();
  @Output() valueClickAction = new EventEmitter();
  @ViewChild('placeholderTag', { static: true }) placeholderTagRef!: ElementRef<HTMLSpanElement>;
  @ViewChild('list', { static: true }) listElementRef!: ElementRef<HTMLUListElement>;
  @ViewChild('addButtonRef') addButtonRef?: ElementRef<HTMLLIElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  private readonly value$$ = new BehaviorSubject<unknown>('');
  private readonly order$$ = new BehaviorSubject<TagOrder>({});
  private readonly fitToContainer$$ = new BehaviorSubject<boolean>(false);
  private readonly resized$$ = new BehaviorSubject<ResizeObserverEntry | undefined>(undefined);

  displayData$: Observable<DisplayData>;

  String = String;

  @Input() set value(v: unknown) {
    this.value$$.next(v);
  }
  get value(): unknown {
    return Array.isArray(this.value$$.value)
      ? this.value$$.value
      : this.value$$.value
        ? [this.value$$.value]
        : null;
  }

  @Input() set order(v: TagOrder | undefined) {
    this.order$$.next(v ?? {});
  }
  get order(): TagOrder {
    return this.order$$.value;
  }

  @Input() set fitToContainer(v: boolean) {
    this.fitToContainer$$.next(v);
  }

  @HostBinding('class.fit-to-container') get isFitToContainer(): boolean {
    return this.fitToContainer$$.value;
  }

  get canAddValue(): boolean {
    return !!(
      this.useExternalClickAction &&
      (this.isMultipleValue || (!this.isMultipleValue && !this.value))
    );
  }
  get addButtonOutside(): boolean {
    return (
      (this.addButtonRef?.nativeElement &&
        (this.addButtonRef?.nativeElement?.classList.contains('add-when-hidden') ||
          this.elementRef?.nativeElement?.offsetWidth <
            this.addButtonRef.nativeElement.offsetLeft)) ||
      false
    );
  }
  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  constructor() {
    this.displayData$ = combineLatest({
      value: this.value$$,
      order: this.order$$,
      fitToContainer: this.fitToContainer$$,
    }).pipe(
      map(({ value, ...rest }) => ({ ...rest, value: this.normalizeValue(value) })),
      switchMap(({ value, order, fitToContainer }) => {
        if (!fitToContainer) {
          return of({ tags: sortBy(value, (item) => order[item]) });
        }

        return combineLatest({
          value: of(value),
          resized: this.resized$$,
        }).pipe(
          /**
           * Emission is throttled here to limit usages of getComputedStyle for element.
           * It is a heavy operation and there can be lots of emissions due to resize effect.
           */
          throttleTime(500, asyncScheduler, { leading: true, trailing: true }),
          map(({ value }) => {
            const bag = this.fitTagsToContainer(value);
            return {
              tags: sortBy(bag?.fitted || value, (item) => order[item]),
              hiddenTagsTooltip: bag?.unfitted.join(', ') || '',
              wrap: (bag?.maxNumberOfLines ?? 1) > 1,
            };
          }),
        );
      }),
    );
  }

  ngAfterViewInit(): void {
    resizeRxUtils
      .fromResizeObserver(this.elementRef.nativeElement)
      .pipe(
        untilDestroyed(this),
        map((entries) => entries[0]),
      )
      .subscribe((value) => this.resized$$.next(value));
  }

  @HostListener('click', ['$event'])
  externalClickAction(event: Event, tag: string, element: HTMLElement): void {
    if (!this.useExternalClickAction) {
      return;
    }
    this.clickAction.emit({ event, tag, element, palette: this.palette });
  }

  externalClickActionRemoveTag(event: Event, tag: string): void {
    this.clickAction.emit({ event, tag });
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  removeTag(event: Event, tag: string): void {
    this.clickAction.emit({ actionType: 'remove', tag } as CellValueUpdateFromTag);
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  valueClick(event: Event, tag: string, element: HTMLElement, index: number): void {
    if (this.useExternalValueClickAction) {
      this.valueClickAction.emit({ event, value: tag, index });
      event.preventDefault();
      event.stopImmediatePropagation();
      this.menuTrigger?.closeMenu();
    } else if (this.useExternalClickAction) {
      this.externalClickAction(event, tag, element);
    }
  }

  preventAction(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private normalizeValue(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (value) {
      return [String(value)];
    }
    return [];
  }

  private getStyleData(): StyleData {
    const { offsetWidth: tagWidth, offsetHeight: tagHeight } = this.placeholderTagRef.nativeElement;
    const { offsetWidth: containerWidth, offsetHeight: containerHeight } =
      this.elementRef.nativeElement;
    const listStyles = window.getComputedStyle(this.listElementRef.nativeElement);

    const tagStyles = window.getComputedStyle(this.placeholderTagRef.nativeElement);
    const tagPaddingX =
      Math.ceil(toFloat(tagStyles.paddingLeft)) + Math.ceil(toFloat(tagStyles.paddingRight));
    const tagGap = Math.ceil(toFloat(tagStyles.gap));
    const tagComputedWidth = Math.ceil(toFloat(tagStyles.width));

    const containerStyles = window.getComputedStyle(this.elementRef.nativeElement);
    const containerPaddingY = Math.floor(
      toFloat(containerStyles.getPropertyValue('--b-t-row-padding')),
    );

    return {
      font: tagStyles.font,
      listGap: Math.ceil(toFloat(listStyles.gap)),
      tagPaddingX: tagPaddingX,
      tagMinWidth: tagWidth - tagPaddingX,
      tagHeight,
      tagGap,
      tagWidth: tagComputedWidth,
      containerWidth: containerWidth - Math.ceil(toFloat(containerStyles.paddingRight)),
      containerHeight: containerHeight - containerPaddingY,
    };
  }

  private fitTagsToContainer(
    tags: string[],
  ): { fitted: string[]; unfitted: string[]; maxNumberOfLines: number } | undefined {
    if (tags.length === 0) {
      return;
    }

    const fitted: string[] = [];
    const styleData = this.getStyleData();
    const maxNumberOfLines = Math.max(
      1,
      Math.floor(styleData.containerHeight / (styleData.tagHeight + styleData.listGap)),
    );
    let linesUsed = 1;
    let remainingLineWidth = styleData.containerWidth;
    let index = 0;

    let currTagWidth;
    do {
      if (tags[index] === undefined) {
        break;
      }
      const tag = tags[index];
      currTagWidth = Math.ceil(this.calculateTagWidth(tag, styleData));
      if (currTagWidth < remainingLineWidth || remainingLineWidth === styleData.containerWidth) {
        // we can insert it into same line when it will fit or line is empty
        fitted.push(tags[index]);
        remainingLineWidth = remainingLineWidth - currTagWidth;
      } else if (linesUsed < maxNumberOfLines) {
        // we can push it into new line
        fitted.push(tags[index]);
        remainingLineWidth = styleData.containerWidth - currTagWidth - styleData.listGap;
        linesUsed++;
      } else {
        // we cannot do anything
        break;
      }
      index++;
    } while (index < tags.length);

    const unfitted = tags.slice(index);

    return {
      fitted,
      unfitted,
      maxNumberOfLines,
    };
  }

  private calculateTagWidth(value: string, styleData: StyleData): number {
    const textWidth = Math.max(styleData.tagMinWidth, measureTextSize(value, styleData.font).width);
    return textWidth + styleData.tagWidth;
  }
}
